import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MdOutlineDarkMode, MdOutlineLightMode } from "react-icons/md";
import { TiUser } from "react-icons/ti";
import ProfileItem from "./ProfileItem/ProfileItem";
import StandingItem from "./StandingItem/StandingItem";
import AccountSwitcher from "./AccountSwitcher/AccountSwitcher"; // 🔄 new modal component
import WalletDropdown from "./Wallet/WalletDropdown";
import "../../../Utils/Css/NavBar.css";
import Mousetrap from "mousetrap";
import { FaWallet } from "react-icons/fa";

import Deposit from "./Wallet/Deposit/Deposit";
import Withdraw from "./Wallet/Withdraw/Withdraw";

export default function NavBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const userType = Number(localStorage.getItem("user_type") || 7);
  const username = localStorage.getItem("username") || "Guest";
  const intraday_sq = Number(localStorage.getItem("intraday_sq") || 0);

  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("themeType") === "1",
  );
  const [fontSize, setFontSize] = useState(
    parseFloat(localStorage.getItem("Font") || 13),
  );
  const [showAccountModal, setShowAccountModal] = useState(false); // ✅ modal state

  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const USER_ROLES = {
    1: "ADMIN",
    2: "SUPER",
    3: "MASTER",
    4: "USER",
    5: "BROKER",
    6: "SUBBROKER",
  };
  const roleLabel = USER_ROLES[userType] || "N/A";

  useEffect(() => {
    document.body.dataset.theme = darkMode ? "dark" : "light";
    localStorage.setItem("themeType", darkMode ? "1" : "0");
  }, [darkMode]);

  useEffect(() => {
    document.documentElement.style.setProperty("--large", `${fontSize}px`);
  }, [fontSize]);

  const toggleTheme = useCallback(() => setDarkMode((prev) => !prev), []);

  const changeFontSize = useCallback((delta) => {
    setFontSize((prev) => {
      const newSize = Math.min(Math.max(prev + delta, 10), 20);
      localStorage.setItem("Font", String(newSize));
      return newSize;
    });
  }, []);

  // Keyboard Shortcuts
  useEffect(() => {
    const shortcuts = {
      "shift+h": "/Home",
      "shift+w": userType === 4 ? "/WatchlistUser" : "/Watchlist",
      "shift+u": "/User",
      "shift+m": "/Master",
      "shift+e": "/Super",
      "shift+b": "/Broker",
      "shift+s": "/Standing",
      "shift+t": "/Trade",
      "shift+p": "/Pending",
      "shift+o": "/Holding",
    };

    Object.entries(shortcuts).forEach(([key, path]) =>
      Mousetrap.bind(key, (e) => {
        e.preventDefault();
        navigate(path);
      }),
    );

    Mousetrap.bind("shift+ctrl+l", (e) => {
      e.preventDefault();
      changeFontSize(1);
    });

    Mousetrap.bind("shift+ctrl+s", (e) => {
      e.preventDefault();
      changeFontSize(-1);
    });

    return () => Mousetrap.reset();
  }, [navigate, userType, changeFontSize]);

  const getSafeLocalStorageJSON = (key, fallback = {}) => {
    try {
      const value = localStorage.getItem(key);
      console.log(JSON.parse(value), "valuevalue")
      if (!value || value === "undefined") return fallback;
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  };

  const routes = [
    {
      label: "ome",
      short: "H",
      path: "/Home",
      show: [1, 2, 3, 5, 6].includes(userType),
    },
    {
      label: "atchlist",
      short: "W",
      path: userType === 4 ? "/WatchlistUser" : "/Watchlist",
      show: true,
    },
    { label: "uper", short: "S", path: "/Super", show: userType === 1 },
    {
      label: "aster",
      short: "M",
      path: "/Master",
      show: [1, 2].includes(userType),
    },
    {
      label: "ser",
      short: "U",
      path: "/User",
      show: [1, 2, 3, 5, 6].includes(userType),
    },
    {
      label: "roker",
      short: "B",
      path: "/Broker",
      show: [1, 2, 3].includes(userType),
    },
    {
      label: "tanding",
      short: "S",
      path: "/Standing",
      show: true,
      hover: [1, 2, 3, 5, 6].includes(userType) ? (
        <StandingItem userType={userType} />
      ) : null,
    },
    { label: "rade", short: "T", path: "/Trade", show: true },
    { label: "ending", short: "P", path: "/Pending", show: true },
    {
      label: "olding",
      short: "H",
      path: "/Holding",
      show: [1, 2, 3, 5, 6].includes(userType),
    },
    {
      label: "allet",
      short: "W",
      path: "/Wallet",
      show: intraday_sq === 1 && [1, 2, 3, 5, 6].includes(userType),
    },
  ];
  return (
    <>
      <div className="NavContainer">
        {/* LEFT SIDE */}
        <div className="NavLeft">
          <img
            src={window.LOGO}
            alt="Logo"
            style={{ height: 60, marginRight: 20 }}
          />
          {routes.map(
            (r) =>
              r.show && (
                <Link
                  key={r.path}
                  to={r.path}
                  className={`NavLinkBtn large ${pathname === r.path ||
                    (userType === 4
                      ? r.path === "/WatchlistUser" && pathname === "/"
                      : r.path === "/Home" && pathname === "/")
                    ? "selectlink"
                    : "unselectlink"
                    }`}
                >
                  <div className="hover-container">
                    <div className="linkname hover-button">
                      <div>{r.short}</div>
                      <div>{r.label}</div>
                    </div>
                    {r.hover && <div className="hover-content">{r.hover}</div>}
                  </div>
                </Link>
              ),
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className="NavRight">
          {/* WALLET */}
          {userType === 4 && intraday_sq === 1 ? (
            <div style={{ marginRight: 12, position: "relative" }}>
              <div className="hover-container">
                <div className="wallet-nav-btn">
                  <FaWallet className="wallet-icon-nav" />
                  <span className="wallet-text large">Wallet</span>
                </div>

                <div
                  className={darkMode ? "dark hover-content" : " hover-content"}
                >
                  <WalletDropdown
                    onOpenDeposit={() => setShowDeposit(true)}
                    onOpenWithdraw={() => setShowWithdraw(true)}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* ACCOUNT SWITCHER BUTTON (opens modal) */}
          <div
            style={{
              marginRight: 10,
              cursor: "pointer",
            }}
            onClick={() => setShowAccountModal(true)} // ✅ open modal
            className="wallet-nav-btn"
          >
            <span className="wallet-text large">
              {roleLabel}: <span className="username">{username}</span>
            </span>
          </div>

          {/* PROFILE */}
          <div style={{ marginRight: 10 }}>
            <div className="hover-container">
              <div className="NavDarkLight">
                <TiUser className="ThemeIcon largeicon" />
              </div>
              <div className="hover-content">
                <ProfileItem userType={userType} />
              </div>
            </div>
          </div>

          {/* THEME TOGGLE */}
          <div
            className="profile-container"
            style={{ marginRight: 10 }}
            onClick={toggleTheme}
          >
            <div className="hover-container NavDarkLight">
              {darkMode ? (
                <MdOutlineLightMode className="ThemeIcon largeicon" />
              ) : (
                <MdOutlineDarkMode className="ThemeIcon largeicon" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ACCOUNT SWITCHER MODAL */}
      {showAccountModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <AccountSwitcher
            userType={userType}
            onClose={() => setShowAccountModal(false)}
          />
        </div>
      )}

      {showDeposit && <Deposit onClose={() => setShowDeposit(false)} />}
      {showWithdraw && <Withdraw onClose={() => setShowWithdraw(false)} />}
    </>
  );
}
